export interface LosVector {
  min_los: number | null;
  max_los: number | null;
  close_to_arrival: boolean;
  close_to_departure: boolean;
}

export interface LosRestrictionJson {
  current: LosVector;
  proposed: LosVector;
  scope?: {
    room_type_id?: string | null;
    rate_group_id?: string | null;
  };
}

export interface LosImpact {
  expected_margin_delta: number | null;
  expected_margin_delta_lcb: number | null;
  confidence: number | null;
  confidence_tier: string | null;
  impact_source: string | null;
  calibration_source: string | null;
  reason_codes: string[];
}

export interface LosOccupancyPoint {
  label: 'D-1' | 'D' | 'D+1' | string;
  offset: number;
  date: string;
  occupancy_pct: number | null;
}

export interface LosItem {
  priority_id: string;
  group_id: string;
  stay_date: string;
  current_los: number;
  proposed_los: number;
  experiment_id?: string | null;
  occupancy_pct?: number | null;
  days_out?: number | null;
  event_name?: string | null;
  reasoning?: string | null;
  impact_score?: number | null;
  restriction_json?: LosRestrictionJson | null;
  impact?: LosImpact | null;
  operator_review_required?: boolean | null;
  occupancy_context?: LosOccupancyPoint[];
}

export type LosGroupedGroup = {
  group_id: string;
  action_types?: string | string[] | null;
};

export interface LosPriorityDetail {
  priority_id: string;
  group_id: string;
  stay_date: string;
  action_type?: string | null;
  current_los?: number | null;
  proposed_los?: number | null;
  current_min_los?: number | null;
  proposed_min_los?: number | null;
  experiment_id?: string | null;
  occupancy_pct?: number | null;
  days_out?: number | null;
  event_name?: string | null;
  reasoning?: string | null;
  impact_score?: number | null;
  restriction_json?: LosRestrictionJson | string | null;
  impact?: Partial<LosImpact> | null;
  operator_review_required?: boolean | null;
  occupancy_context?: LosOccupancyPoint[] | null;
}

export interface LosGroupDetailsResponse {
  priorities: LosPriorityDetail[];
}

export interface LosGroupBatchResponse {
  groups: Array<{
    group_id: string;
    property_id?: string | null;
    priorities: LosGroupDetailsResponse['priorities'];
  }>;
  missing_group_ids?: string[];
  error?: string;
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function toStringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function toStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || '').trim()).filter(Boolean);
  }
  return [];
}

function toBooleanOrNull(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return null;
}

function normalizeLosImpact(raw: Partial<LosImpact> | null | undefined): LosImpact | null {
  if (!raw || typeof raw !== 'object') return null;
  return {
    expected_margin_delta: toNumber(raw.expected_margin_delta),
    expected_margin_delta_lcb: toNumber(raw.expected_margin_delta_lcb),
    confidence: toNumber(raw.confidence),
    confidence_tier: toStringOrNull(raw.confidence_tier),
    impact_source: toStringOrNull(raw.impact_source),
    calibration_source: toStringOrNull(raw.calibration_source),
    reason_codes: toStringList(raw.reason_codes),
  };
}

export const LOS_REASON_LABELS: Record<string, string> = {
  COMPRESSION_PROTECTION: 'Peak-night protection',
  SHOULDER_CAPTURE: 'Pulls stays into soft shoulder',
  CLOSE_IN_RELAXATION: 'Close-in: restrictions lifted',
  GAP_FILL: 'Fills an orphan gap',
  EVENT_COMPRESSION: 'Event compression',
  ANCHOR_EVENT: 'Anchor event',
  PACE_ACCELERATING: 'Pace accelerating',
  PACE_FADING: 'Demand fading',
  PROMO_ACTIVE_HOLD: 'Held: promo active',
  MIN_LOS_INVENTORY_CONTIGUITY_BLOCK: 'Capped by availability',
  DATA_LOW_CONFIDENCE: 'Low data confidence',
};

export function labelLosReasonCode(code: string): string {
  const normalized = String(code || '').trim().toUpperCase();
  if (!normalized) return '';
  return (
    LOS_REASON_LABELS[normalized] ??
    normalized
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
      .join(' ')
  );
}

export type LosImpactSourceBadge =
  | { label: 'heuristic'; tone: 'muted' }
  | { label: 'learned'; tone: 'learned' };

export function classifyLosImpactSource(calibrationSource: string | null | undefined): LosImpactSourceBadge {
  const normalized = String(calibrationSource || '').trim().toLowerCase();
  if (normalized === 'placeholder' || normalized === 'bid_price_heuristic') {
    return { label: 'heuristic', tone: 'muted' };
  }
  if (
    normalized === 'learned' ||
    normalized === 'los_outcome_summary' ||
    normalized === 'simulated' ||
    normalized === 'los_economic_evaluator'
  ) {
    return { label: 'learned', tone: 'learned' };
  }
  return { label: 'heuristic', tone: 'muted' };
}

function toLosVector(raw: unknown): LosVector {
  const source = (raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}) as Record<
    string,
    unknown
  >;
  return {
    min_los: toNumber(source.min_los),
    max_los: toNumber(source.max_los),
    close_to_arrival: Boolean(source.close_to_arrival),
    close_to_departure: Boolean(source.close_to_departure),
  };
}

export function parseRestrictionJson(
  raw: LosRestrictionJson | string | null | undefined,
): LosRestrictionJson | null {
  if (!raw) return null;
  let source: Record<string, unknown> | null = null;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        source = parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
  } else if (typeof raw === 'object') {
    source = raw as unknown as Record<string, unknown>;
  }
  if (!source) return null;
  return {
    current: toLosVector(source.current),
    proposed: toLosVector(source.proposed),
    scope:
      source.scope && typeof source.scope === 'object'
        ? {
            room_type_id:
              ((source.scope as Record<string, unknown>).room_type_id as string | null) ?? null,
            rate_group_id:
              ((source.scope as Record<string, unknown>).rate_group_id as string | null) ?? null,
          }
        : undefined,
  };
}

export function losVectorHasNonMinChange(restriction: LosRestrictionJson | null): boolean {
  if (!restriction) return false;
  const { current, proposed } = restriction;
  return (
    (current.max_los ?? null) !== (proposed.max_los ?? null) ||
    current.close_to_arrival !== proposed.close_to_arrival ||
    current.close_to_departure !== proposed.close_to_departure
  );
}

export interface LosVectorRow {
  key: 'min_los' | 'max_los' | 'cta' | 'ctd';
  label: string;
  current: string;
  proposed: string;
  changed: boolean;
}

function formatLosCount(value: number | null): string {
  return value == null ? '—' : String(value);
}

function formatGateState(value: boolean): string {
  return value ? 'closed' : 'open';
}

export function buildLosVectorRows(restriction: LosRestrictionJson | null): LosVectorRow[] {
  if (!restriction) return [];
  const { current, proposed } = restriction;
  return [
    {
      key: 'min_los',
      label: 'MinLOS',
      current: formatLosCount(current.min_los),
      proposed: formatLosCount(proposed.min_los),
      changed: (current.min_los ?? null) !== (proposed.min_los ?? null),
    },
    {
      key: 'max_los',
      label: 'MaxLOS',
      current: formatLosCount(current.max_los),
      proposed: formatLosCount(proposed.max_los),
      changed: (current.max_los ?? null) !== (proposed.max_los ?? null),
    },
    {
      key: 'cta',
      label: 'CTA',
      current: formatGateState(current.close_to_arrival),
      proposed: formatGateState(proposed.close_to_arrival),
      changed: current.close_to_arrival !== proposed.close_to_arrival,
    },
    {
      key: 'ctd',
      label: 'CTD',
      current: formatGateState(current.close_to_departure),
      proposed: formatGateState(proposed.close_to_departure),
      changed: current.close_to_departure !== proposed.close_to_departure,
    },
  ];
}

function normalizeActionTypeEntry(value: unknown): string {
  return String(value || '')
    .trim()
    .toUpperCase();
}

export function normalizeActionTypes(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(normalizeActionTypeEntry).filter(Boolean);
  }
  if (typeof value !== 'string') return [];
  return value.split(',').map(normalizeActionTypeEntry).filter(Boolean);
}

export function isLosActionType(value: unknown): boolean {
  return normalizeActionTypes(value).some((actionType) => actionType.includes('LOS'));
}

export function extractLosItemFromPriorities(
  priorities: LosGroupDetailsResponse['priorities'] | null | undefined,
): LosItem | null {
  const losPriority = (priorities || []).find((priority) => {
    if (isLosActionType(priority.action_type)) return true;
    return priority.proposed_los != null || priority.proposed_min_los != null;
  });
  if (!losPriority) return null;

  const proposedLos = toNumber(losPriority.proposed_los ?? losPriority.proposed_min_los);
  if (proposedLos == null) return null;

  return {
    priority_id: losPriority.priority_id,
    group_id: losPriority.group_id,
    stay_date: losPriority.stay_date,
    current_los: toNumber(losPriority.current_los ?? losPriority.current_min_los) ?? 1,
    proposed_los: proposedLos,
    experiment_id: losPriority.experiment_id ?? null,
    occupancy_pct: toNumber(losPriority.occupancy_pct),
    days_out: toNumber(losPriority.days_out),
    event_name: losPriority.event_name ?? null,
    reasoning: losPriority.reasoning ?? null,
    impact_score: toNumber(losPriority.impact_score),
    restriction_json: parseRestrictionJson(losPriority.restriction_json),
    impact: normalizeLosImpact(losPriority.impact),
    operator_review_required: toBooleanOrNull(losPriority.operator_review_required),
    occupancy_context: Array.isArray(losPriority.occupancy_context)
      ? losPriority.occupancy_context
          .map((point) => ({
            label: String(point.label || ''),
            offset: toNumber(point.offset) ?? 0,
            date: String(point.date || ''),
            occupancy_pct: toNumber(point.occupancy_pct),
          }))
          .filter((point) => point.label && point.date)
      : [],
  };
}

export function buildLosItems(groups: LosGroupedGroup[], batchData: LosGroupBatchResponse): LosItem[] {
  const groupMap = new Map((batchData.groups || []).map((group) => [group.group_id, group]));
  const detailResponses = groups.map((group) => {
    const detailData = groupMap.get(group.group_id);
    const losItem = extractLosItemFromPriorities(detailData?.priorities);
    if (!losItem) return null;
    return losItem;
  });

  const nextItems = detailResponses.filter(Boolean) as LosItem[];
  nextItems.sort((a, b) => {
    const aDate = Date.parse(a.stay_date);
    const bDate = Date.parse(b.stay_date);
    if (Number.isFinite(aDate) && Number.isFinite(bDate) && aDate !== bDate) {
      return aDate - bDate;
    }
    if (Number.isFinite(aDate) && !Number.isFinite(bDate)) return -1;
    if (!Number.isFinite(aDate) && Number.isFinite(bDate)) return 1;
    return (b.impact_score || 0) - (a.impact_score || 0);
  });
  return nextItems;
}
